import { useEffect, useMemo, useState } from 'react';
import { DateRange } from 'react-date-range';
import { format as formatFns } from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import {
  Box,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
} from '@mui/material';
import { CalendarMonth as CalendarMonthIcon } from '@mui/icons-material';

import { useIsMobile } from '../../utils/responsive';

function ymdToDate(ymd) {
  if (!ymd) return new Date();
  const parts = String(ymd).split('-').map((n) => Number(n));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return new Date();
  const [y, m, d] = parts;
  return new Date(y, Math.max(0, (m || 1) - 1), d || 1);
}

function dateToYmd(date) {
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDmy(ymd) {
  try {
    return formatFns(ymdToDate(ymd), 'dd.MM.yyyy', { locale: ru });
  } catch {
    return String(ymd || '');
  }
}

/**
 * Унифицированный выбор диапазона дат:
 * - одно поле "Период"
 * - Popover с 2 календарями и подсветкой диапазона
 *
 * valueFrom/valueTo: строки в формате YYYY-MM-DD
 * onChange: ({ from, to }) => void
 */
export default function DateRangeField({
  label = 'Период',
  valueFrom,
  valueTo,
  onChange,
  size = 'small',
  fullWidth = true,
  disabled = false,
}) {
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState(null);

  const [range, setRange] = useState([
    { startDate: ymdToDate(valueFrom), endDate: ymdToDate(valueTo), key: 'selection' },
  ]);

  useEffect(() => {
    setRange([{ startDate: ymdToDate(valueFrom), endDate: ymdToDate(valueTo), key: 'selection' }]);
  }, [valueFrom, valueTo]);

  const open = Boolean(anchorEl);

  const displayValue = useMemo(() => {
    return `${formatDmy(valueFrom)} – ${formatDmy(valueTo)}`;
  }, [valueFrom, valueTo]);

  const handleOpen = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleChange = (item) => {
    const sel = item?.selection;
    if (!sel?.startDate || !sel?.endDate) return;
    setRange([sel]);
    onChange?.({ from: dateToYmd(sel.startDate), to: dateToYmd(sel.endDate) });
  };

  return (
    <>
      <TextField
        label={label}
        value={displayValue}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        onClick={handleOpen}
        inputProps={{ readOnly: true }}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpen(e);
                }}
                edge="end"
                disabled={disabled}
              >
                <CalendarMonthIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 1,
            maxWidth: 'min(100vw, 980px)',
            overflowX: 'auto',
          },
        }}
      >
        <Box sx={{ minWidth: isMobile ? 320 : 0 }}>
          <DateRange
            ranges={range}
            onChange={handleChange}
            months={2}
            direction={isMobile ? 'vertical' : 'horizontal'}
            moveRangeOnFirstSelection={false}
            showPreview={false}
            showDateDisplay={false}
            editableDateInputs={false}
            locale={ru}
          />
        </Box>
      </Popover>
    </>
  );
}

